import React from 'react';
import { Building2, ChevronRight, Lock, Trash2 } from 'lucide-react';
import { Tooltip } from '@cotera/client/app/components/ui/tooltip';
import { useWhoami } from '@cotera/client/app/stores/org';
import { Button } from '@cotera/client/v0/components/ui/button';
import { MemberAvatar } from '../list/member-avatar';
import {
  permissionLabel,
  SharePermissionSelect,
  type SharePermission,
  toSharePermission,
} from './share-permission-select';
import { type CombinedAccess } from './use-share-access';

/** Who can open it beyond the people listed, for the general access row. */
export type GeneralAccess =
  /** `limitedIn`: the folder it's in but doesn't follow the sharing of. */
  | { kind: 'invited'; limitedIn: string | null }
  | { kind: 'org'; permission: SharePermission; onCount: number }
  | { kind: 'folder'; permission: SharePermission; folderName: string };

/**
 * The first row under "Who has access": whether anyone beyond the people
 * listed can open it. Opens the general access page for those who manage it.
 */
export const GeneralAccessRow: React.FC<{
  state: GeneralAccess;
  orgName: string;
  total: number;
  /** Everyone added besides the owner, invites not opened yet included. */
  peopleCount: number;
  onOpen: (() => void) | undefined;
  /** A menu for the row itself, in place of opening settings. */
  trailing?: React.ReactNode;
}> = ({ state, orgName, total, peopleCount, onOpen, trailing }) => {
  const Icon = state.kind === 'invited' ? Lock : Building2;
  const title =
    state.kind === 'invited' ? 'Only people added' : `Everyone at ${orgName}`;
  const detail =
    state.kind === 'invited'
      ? [
          peopleCount === 0
            ? 'Just you'
            : `${peopleCount} ${peopleCount === 1 ? 'person' : 'people'}`,
          state.limitedIn === null
            ? null
            : `not shared through the folder “${state.limitedIn}”`,
        ]
          .filter((part): part is string => part !== null)
          .join(' · ')
      : state.kind === 'org'
      ? [`Can ${state.permission}`, coverage(state.onCount, total)]
          .filter((part): part is string => part !== null)
          .join(' · ')
      : `Can ${state.permission} · through the folder “${state.folderName}”`;
  const body = (
    <>
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-foreground">
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{title}</span>
        <span className="block truncate text-xs text-muted-foreground">
          {detail}
        </span>
      </span>
    </>
  );
  return (
    <li>
      {trailing !== undefined ? (
        <div className="flex items-center gap-3 py-1.5">
          {body}
          {trailing}
        </div>
      ) : onOpen === undefined ? (
        <div className="flex items-center gap-3 py-1.5">{body}</div>
      ) : (
        <button
          type="button"
          onClick={onOpen}
          aria-label={`Who has access: ${title}. Change`}
          className="-mx-1 flex w-[calc(100%+0.5rem)] items-center gap-3 rounded-lg px-1 py-1.5 text-left outline-none transition-colors hover:bg-muted/60 focus-visible:ring-[3px] focus-visible:ring-ring/50"
        >
          {body}
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      )}
    </li>
  );
};

// ---------------------------------------------------------------------------
// People with access: the one place that says who can open this
// ---------------------------------------------------------------------------

const coverage = (onCount: number, total: number): string | null =>
  onCount < total ? `on ${onCount} of ${total} items` : null;

/**
 * The people rows under "Who has access": the owner, everyone added, and who
 * gets in through a containing folder. The org is the general access row.
 */
export const PeopleWithAccess: React.FC<{
  access: CombinedAccess;
  canManage: boolean;
  allowView: boolean;
  disabled: boolean;
  onChange: (userId: string, permission: SharePermission) => Promise<void>;
  onRemove: (userId: string, isSelf: boolean) => Promise<void>;
  onRemoveInvite: (
    invite: CombinedAccess['pendingInvites'][number]
  ) => Promise<void>;
  /** Stop sharing with another org that opened a link to it. */
  onRemoveOrg: (org: CombinedAccess['externalOrgs'][number]) => Promise<void>;
}> = ({
  access,
  canManage,
  allowView,
  disabled,
  onChange,
  onRemove,
  onRemoveInvite,
  onRemoveOrg,
}) => {
  const meId = useWhoami((s) => s.whoami.id);
  const directIds = new Set(access.people.map((p) => p.userId));
  return (
    <>
      <PersonRow
        userId={access.owner.userId}
        name={access.owner.name}
        email={access.owner.email}
        isSelf={access.owner.userId === meId}
        trailing={<Pill>Owner</Pill>}
      />
      {access.people.map((person) => {
        const isSelf = person.userId === meId;
        const level: SharePermission = toSharePermission(person.permission);
        return (
          <PersonRow
            key={person.userId}
            userId={person.userId}
            name={person.name}
            email={person.email}
            detail={coverage(person.onCount, access.total)}
            isSelf={isSelf}
            badge={
              person.external ? (
                <Tooltip
                  side="bottom"
                  tooltipContent={`Joined from ${
                    person.externalOrgName ?? 'another organization'
                  } through a link`}
                >
                  <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                    External
                  </span>
                </Tooltip>
              ) : null
            }
            trailing={
              canManage ? (
                <SharePermissionSelect
                  value={level}
                  allowView={allowView}
                  disabled={disabled}
                  onChange={(next) => void onChange(person.userId, next)}
                  onRemove={() => void onRemove(person.userId, isSelf)}
                  removeLabel={isSelf ? 'Leave' : 'Remove access'}
                  aria-label={`Access for ${
                    person.name ?? person.email ?? 'this person'
                  }`}
                />
              ) : isSelf ? (
                <span className="flex items-center gap-1">
                  <Pill>{permissionLabel(level)}</Pill>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    disabled={disabled}
                    onClick={() => void onRemove(person.userId, true)}
                  >
                    Leave
                  </Button>
                </span>
              ) : (
                <Pill>{permissionLabel(level)}</Pill>
              )
            }
          />
        );
      })}
      {access.pendingInvites.map((invite) => (
        <li key={invite.linkId} className="flex items-center gap-3 py-1.5">
          {/* Dashed: added, but hasn't opened it yet. */}
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-dashed border-muted-foreground/50 text-xs font-medium uppercase text-muted-foreground">
            {invite.email.slice(0, 1)}
          </span>
          <p className="min-w-0 flex-1 truncate text-sm">
            {invite.email}
            <span className="text-muted-foreground"> (Added)</span>
          </p>
          {canManage ? (
            <Tooltip side="left" tooltipContent="Remove">
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Remove ${invite.email}`}
                disabled={disabled}
                onClick={() => void onRemoveInvite(invite)}
                className="text-muted-foreground hover:text-foreground"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </Tooltip>
          ) : null}
        </li>
      ))}
      {access.inherited.map((folder) => {
        const through = `Through the folder “${folder.folderName}”`;
        return (
          <React.Fragment key={folder.folderId}>
            {folder.people
              .filter((person) => !directIds.has(person.userId))
              .map((person) => (
                <PersonRow
                  key={`${folder.folderId}:${person.userId}`}
                  userId={person.userId}
                  name={person.name}
                  email={null}
                  detail={through}
                  isSelf={person.userId === meId}
                  trailing={
                    <Tooltip
                      side="bottom"
                      tooltipContent="Gets in through the folder. To take them off this, choose Only people added in share settings."
                    >
                      <Pill>
                        {permissionLabel(toSharePermission(person.permission))}
                      </Pill>
                    </Tooltip>
                  }
                />
              ))}
          </React.Fragment>
        );
      })}
      {canManage
        ? access.externalOrgs.map((externalOrg) => {
            const name = externalOrg.name ?? 'Another organization';
            return (
              <li
                key={externalOrg.orgId}
                className="flex items-center gap-3 py-1.5"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium uppercase text-muted-foreground">
                  {name.slice(0, 1)}
                </span>
                <p className="min-w-0 flex-1 truncate text-sm">
                  Everyone at {name}
                  <span className="text-muted-foreground">
                    {' '}
                    (through a link)
                  </span>
                </p>
                <Pill>
                  {permissionLabel(toSharePermission(externalOrg.permission))}
                </Pill>
                <Tooltip side="left" tooltipContent="Remove">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Stop sharing with ${name}`}
                    disabled={disabled}
                    onClick={() => void onRemoveOrg(externalOrg)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </Tooltip>
              </li>
            );
          })
        : null}
      {!canManage && access.externalOrgs.length > 0 ? (
        <li className="pt-1 text-xs text-muted-foreground">
          Also shared with{' '}
          {access.externalOrgs
            .map((o) => o.name ?? 'another organization')
            .join(', ')}
          .
        </li>
      ) : null}
    </>
  );
};

export const Pill: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground">
    {children}
  </span>
);

const PersonRow: React.FC<{
  userId: string;
  name: string | null;
  email: string | null;
  /** Said after the email: where the access comes from, or how far it reaches. */
  detail?: string | null;
  isSelf: boolean;
  badge?: React.ReactNode;
  trailing: React.ReactNode;
}> = ({ userId, name, email, detail = null, isSelf, badge, trailing }) => {
  const label = name ?? email ?? 'Unknown member';
  const secondLine = [name !== null ? email : null, detail]
    .filter((part): part is string => part !== null && part !== '')
    .join(' · ');
  return (
    <li className="flex items-center gap-3 py-1.5">
      <MemberAvatar
        seed={userId}
        label={label}
        className="h-9 w-9"
        textClassName="text-xs"
      />
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1.5 truncate text-sm font-medium">
          <span className="truncate">
            {label}
            {isSelf ? (
              <span className="font-normal text-muted-foreground"> (you)</span>
            ) : null}
          </span>
          {badge}
        </p>
        {secondLine !== '' ? (
          <p className="truncate text-xs text-muted-foreground">{secondLine}</p>
        ) : null}
      </div>
      {trailing}
    </li>
  );
};
